import { useState, useEffect } from "react";
import posthog from "posthog-js";
import {
    fetchSubscriptionData,
    isSubscriptionSuspended,
    HRM_SUBSCRIPTION_CHANGED_EVENT,
} from "../utils/subscriptionUtils";

/**
 * Hook to check subscription status
 * @returns {Object} - { 
 *   isSuspended, 
 *   subscriptionData, 
 *   loading,
 *   isCancelledButBillingActive,
 *   billingPeriodEnd,
 *   daysRemainingInBillingPeriod,
 *   isActive
 * }
 */
export const useSubscriptionStatus = () => {
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [isSuspended, setIsSuspended] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkSubscription = async (options = {}) => {
            const force = options.force === true;
            try {
                setLoading(true);
                const data = await fetchSubscriptionData(force ? { force: true } : {});
                setSubscriptionData(data);

                // Single source of truth: subscription entity + flags (isActive alone can disagree with status)
                if (!data || !data.subscription) {
                    setIsSuspended(true);
                } else {
                    setIsSuspended(isSubscriptionSuspended(data));
                }
            } catch (error) {
                if (typeof posthog !== "undefined" && posthog.capture) {
                    posthog.capture("checkSubscriptionStatus", {
                        type: "exception",
                        success: false,
                        reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                        status: error?.response?.status,
                    });
                }
                // If subscription fetch fails (e.g., subscription not found), suspend the account
                setIsSuspended(true);
            } finally {
                setLoading(false);
            }
        };

        checkSubscription({ force: false });

        const onSubscriptionChanged = () => {
            void checkSubscription({ force: true });
        };
        if (typeof window !== "undefined") {
            window.addEventListener(HRM_SUBSCRIPTION_CHANGED_EVENT, onSubscriptionChanged);
        }
        return () => {
            if (typeof window !== "undefined") {
                window.removeEventListener(HRM_SUBSCRIPTION_CHANGED_EVENT, onSubscriptionChanged);
            }
        };
    }, []);

    // Extract additional subscription status information
    const isCancelledButBillingActive = subscriptionData?.isCancelledButBillingActive ?? false;
    const billingPeriodEnd = subscriptionData?.billingPeriodEnd ?? null;
    const daysRemainingInBillingPeriod = subscriptionData?.daysRemainingInBillingPeriod ?? 0;
    const isActive = subscriptionData?.isActive ?? false;

    return {
        isSuspended,
        subscriptionData,
        loading,
        isCancelledButBillingActive,
        billingPeriodEnd,
        daysRemainingInBillingPeriod,
        isActive
    };
};

